import React from 'react'
import { cn } from '../../lib/utils'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'warning' | 'destructive' | 'outline' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  children: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', children, ...props }, ref) => {
    const baseClasses = 'inline-flex items-center justify-center rounded-xl font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]'
    
    const variants = {
      primary: 'bg-midnight-black text-white hover:bg-black focus:ring-midnight-black shadow-sm border border-transparent',
      secondary: 'bg-stone text-midnight-blue hover:bg-gray-200 focus:ring-stone border border-transparent',
      warning: 'bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-500 border border-transparent',
      destructive: 'bg-white text-brand-coral border border-brand-coral hover:bg-brand-coral/5 focus:ring-brand-coral',
      outline: 'border border-gray-200 text-midnight-blue bg-white hover:bg-gray-50 hover:border-gray-300 focus:ring-midnight-blue',
      ghost: 'text-midnight-blue/40 bg-transparent hover:bg-midnight-blue/5 hover:text-midnight-blue focus:ring-midnight-blue transition-colors'
    }
    
    const sizes = {
      sm: 'px-4 py-2 text-[19px] min-h-[40px] rounded-lg',
      md: 'px-6 py-3 text-[21px] min-h-[48px] rounded-xl',
      lg: 'px-8 py-4 text-[24px] min-h-[56px] rounded-xl'
    }
    
    return (
      <button
        className={cn(
          baseClasses,
          variants[variant],
          sizes[size],
          className
        )}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }
